import { z } from 'zod';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AtleastOne<T extends Record<string, any>> = keyof T extends infer K
  ? K extends string
  ? Pick<T, K & keyof T> & Partial<T>
  : never
  : never;

export const classificationSchema = z.enum(['Default', 'Mandatory']).nullable();
export type Classification = z.infer<typeof classificationSchema>;

export const departmentSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.number(),
  is_active: z.boolean(),
  classification: classificationSchema,
});
export type Department = z.infer<typeof departmentSchema>;

export const reportItemSchema = z.object({
  reference_id: z.string(),
  reports_type_name: z.string(),
  priority: z.number(),
  country_verbose: z.object({
    code: z.string(),
    name: z.string(),
  }),
  status: z.number(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  assignee: z.number().nullable(),
  assignee_full_name: z.string().nullish(),
  due_date: z.string().datetime().nullable(),
  final_due_date: z.string().datetime().nullable(),
  user_full_name: z.string(),
  company_name: z.string(),
  tenant_has_unread_message: z.boolean().nullable(),
  formatted_report_number: z.string().nullable(),
});
export type ReportItem = z.infer<typeof reportItemSchema>;

export const reportDetailPreviewSchema = z.object({
  id: z.string(),
  user_full_name: z.string(),
  description: z.string(),
  status: z.number(),
  status_label: z.string(),
});
export type ReportDetailPreview = z.infer<typeof reportDetailPreviewSchema>;

export const CountryVerboseSchema = z.object({
  code: z.string(),
  name: z.string(),
});
export type CountryVerbose = z.infer<typeof CountryVerboseSchema>;

export const TenantReportSchema = z.object({
  id: z.string(),
  reference_id: z.string(),
  full_name: z.union([z.null(), z.string()]),
});
export type TenantReport = z.infer<typeof TenantReportSchema>;

export const auditLogSchema = z.object({
  author: z.object({
    full_name: z.string().nullable(),
  }),
  activity: z.string(),
  created_at: z.string().datetime(),
});
export type AuditLog = z.infer<typeof auditLogSchema>;

export const noteCategorySchema = z.object({
  id: z.number(),
  name: z.string(),
  selected: z.boolean().optional().nullable(),
});
export type NoteCategory = z.infer<typeof noteCategorySchema>;

export const meetingNoteSchema = z.object({
  id: z.number(),
  author: z.string(),
  meeting: z.string(),
  description: z.string(),
  category: noteCategorySchema.nullable(),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date(),
});
export type MeetingNote = z.infer<typeof meetingNoteSchema>;
const updateMeetingNotePayload = meetingNoteSchema
  .omit({
    id: true,
    category: true,
    created_at: true,
    updated_at: true,
  })
  .extend({ category: z.number() });
export type UpdateMeetingNotePayload = Pick<MeetingNote, 'id'> &
  AtleastOne<z.infer<typeof updateMeetingNotePayload>>;

export const internalCommentSchema = z.object({
  id: z.number(),
  author: z.object({
    id: z.number(),
    full_name: z.string(),
  }),
  category: noteCategorySchema.nullable(),
  content: z.string(),
  created_at: z.string().datetime(),
});
export type InternalComment = z.infer<typeof internalCommentSchema>;

const internalNoteSchema = z.union([internalCommentSchema, meetingNoteSchema]);
export type InternalNote = z.infer<typeof internalNoteSchema>;

export const commentUserSchema = z.object({
  id: z.number(),
  full_name: z.string().nullable(),
});

export type CommentUser = z.infer<typeof commentUserSchema>;

const baseReportCommentSchema = z.object({
  id: z.number(),
  report: z.string(),
  author: z.number(),
  author_details: commentUserSchema,
  content: z.string(),
  tagged_users: z.array(z.number()),
  tagged_users_details: z.array(commentUserSchema),
  has_seen_users: z.array(z.number()),
  parent: z.coerce.number().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  is_resolved: z.boolean(),
});

export type ReportComment = z.infer<typeof baseReportCommentSchema> & {
  replies: ReportComment[];
};

//https:github.com/colinhacks/zod#recursive-types
export const reportCommentSchema: z.ZodType<ReportComment> =
  baseReportCommentSchema.extend({
    replies: z.lazy(() => z.array(reportCommentSchema)),
  });
export const transcriptSchema = z.object({
  id: z.number(),
  audio_recording: z.string(),
  transcription: z.string(),
  created_at: z.string().datetime(),
});
export type Transcript = z.infer<typeof transcriptSchema>;

export const reportUploadedFileSchema = z.object({
  id: z.number(),
  original_file_name: z.string(),
  attachment_type: z.number(),
  file_type: z.string(),
  url: z.string(),
  upload_finished_at: z.string().datetime(),
  uploaded_by: z.null(),
  user_full_name: z.string().nullable(),
  user_type: z.number().nullable(),
});
export type ReportUploadedFile = z.infer<typeof reportUploadedFileSchema>;

export const reportInformationSchema = z.object({
  uploader_id: z.null(),
  uploader_name: z.null(),
  files: z.array(reportUploadedFileSchema),
});

export type ReportInformationElement = z.infer<typeof reportInformationSchema>;

export const reportDetailSchema = z.object({
  reference_id: z.string(),
  priority: z.number(),
  report_type: z.object({
    id: z.number(),
    name: z.string(),
    type: z.number(),
    is_active: z.boolean(),
    classification: z.string(),
    is_test: z.boolean(),
    is_auto_assignment_enabled: z.boolean(),
    auto_assignment_department: z.null(),
    auto_assignment_user: z.null(),
    is_non_whistleblowing: z.boolean(),
  }),
  due_date: z.string().datetime(),
  assignee: z
    .object({
      id: z.number(),
      full_name: z.string().nullable(),
    })
    .nullable(),
  followers: z.array(z.any()),
  tags: z.array(z.any()),
  status: z.number(),
  user_full_name: z.string(),
  linked_reports: z.array(z.any()),
  is_description_redacted: z.boolean(),
  additional_information: z.array(
    z.object({ description_text: z.string().nullable() }),
  ),
  report_informations: z.array(reportInformationSchema),
  user_tenants: z.array(
    z.object({
      id: z.number(),
      full_name: z.union([z.null(), z.string()]),
    }),
  ),
  tenant_reports: z.array(TenantReportSchema),
  internal_comments: internalCommentSchema.array(),
  audit_logs: z.array(auditLogSchema),
  conversation: z.string(),
  department: departmentSchema.nullable(),
  suspected_department: z.string().nullable(),
  subject_individual: z.string(),
  report_custom_fields: z.array(
    z.object({
      title: z.string(),
      value: z.string(),
      deleted_at: z.string().datetime().nullable(),
    }),
  ),
  country_verbose: z.object({
    code: z.string(),
    name: z.string(),
  }),
  is_case_locked_enabled: z.boolean(),
  created_at: z.string().datetime(),
  id: z.string(),
  is_whistleblower_feedback_enabled: z.boolean(),
  completed_date: z.string().datetime(),
  is_internally_submitted: z.boolean(),
  is_closed: z.boolean(),
  final_due_date: z.string().datetime(),
  company_name: z.string(),
  tenant_has_unread_message: z.boolean(),
  is_assignee: z.boolean(),
  description_detected_language_ids: z.array(z.number()),
  transcripts: transcriptSchema.array(),
  report_outcome: z.string().nullable(),
  employee_sanction: z.string().nullable(),
  report_action_fields_value: z.array(
    z.object({
      id: z.number(),
      value: z.string(),
      field_title: z.string(),
      field_id: z.number(),
    }),
  ),
  loss_value: z.string().nullable(),
  loss_value_currency: z.string(),
  formatted_report_number: z.string(),
  reporter_last_login: z.string().nullable(),
  description: z.object({
    id: z.coerce.number(),
    can_edit_risk_score: z.boolean(),
    risk_score: z.coerce.number(),
    description: z.string(),
  }),
  is_showing_redacted_description: z.boolean(),
  archived_fields: z.array(z.any()),
  potential_report_links: z.array(
    z.object({
      reference_id: z.string(),
      formatted_report_number: z.string(),
    }),
  ),
});
export type ReportDetail = z.infer<typeof reportDetailSchema>;

export const reportTypeSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.number(),
});
export type ReportType = z.infer<typeof reportTypeSchema>;

export const ActiveCountryChoiceSchema = z.object({
  code: z.string(),
  name: z.string(),
});
export type ActiveCountryChoice = z.infer<typeof ActiveCountryChoiceSchema>;

export const reportCustomFieldSchema = z.object({
  id: z.number(),
  title: z.string(),
  type: z.number(),
  dropdown_choices: z.array(z.coerce.string()),
  allow_multi_value: z.boolean(),
  is_required: z.boolean(),
  deleted_at: z.boolean().nullable(),
  sequence: z.number(),
  allow_anonymous_submission: z.boolean(),
  help_text: z.string().nullable(),
  custom_field_conditional_logic: z
    .object({
      id: z.number(),
      label: z.string(),
      matches_value: z.string(),
      report_custom_field: z.number(),
      type: z.number(),
      dropdown_choices: z.array(z.string()),
    })
    .nullable(),
});
export type ReportCustomField = z.infer<typeof reportCustomFieldSchema>;

export const ReportTypeElementSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.number(),
  is_active: z.boolean(),
  classification: classificationSchema,
  is_test: z.boolean(),
  is_auto_assignment_enabled: z.boolean(),
  auto_assignment_department: z.union([z.number(), z.null()]),
  auto_assignment_user: z.union([z.number(), z.null()]),
  is_non_whistleblowing: z.boolean(),
});
export type ReportTypeElement = z.infer<typeof ReportTypeElementSchema>;

export const TenantUserSchema = z.object({
  id: z.number(),
  full_name: z.union([z.null(), z.string()]),
});
export type TenantUser = z.infer<typeof TenantUserSchema>;

export const reportFormSchema = z.object({
  report_types: z.array(ReportTypeElementSchema),
  departments: z.array(departmentSchema),
  is_anonymous_reporting_enabled: z.boolean(),
  report_custom_fields: z.array(reportCustomFieldSchema),
  active_country_choices: z.array(ActiveCountryChoiceSchema),
  inactive_country_choices: z.array(z.any()),
  tenant_users: z.array(TenantUserSchema),
  is_show_non_whistleblowing_message: z.boolean(),
  non_whistleblowing_message: z.string().nullable(),
});
export type ReportForm = z.infer<typeof reportFormSchema>;

const reportFormConfigSchema = reportFormSchema.pick({
  is_anonymous_reporting_enabled: true,
  non_whistleblowing_message: true,
  is_show_non_whistleblowing_message: true,
  report_custom_fields: true,
});
export type ReportFormConfig = z.infer<typeof reportFormConfigSchema>;

export const informedIdividualSchema = z.object({
  id: z.number(),
  report: z.string(),
  name: z.string(),
  associated_department: z.number(),
  notification_reason: z.string(),
  date_informed_at: z.string(),
  is_deleted: z.boolean(),
});
export type InformedIndividual = z.infer<typeof informedIdividualSchema>;

export const interviewQuestionSchema = z.object({
  id: z.number(),
  report: z.string(),
  author: z.number(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  question: z.string(),
  order: z.number(),
});
export type InterviewQuestion = z.infer<typeof interviewQuestionSchema>;

export interface AttachmentFile {
  id: number;
  name: string;
  file_type: string;
  file_size?: number;
  created_at: string;
  last_modified: string;
  url: string;
  attachment_type: number | null;
  user_type: number;
  uploader_id: number;
  uploader_name: string;
  is_folder: false; // Always false for files
  is_checked?: boolean;
}

export interface AttachmentFolder {
  id: number;
  name: string;
  file_type: 'folder';
  attachment_type: null;
  last_modified: string;
  created_at: string;
  user_type: number;
  uploader_id: number;
  parent_folder: string | null;
  uploader_name: string;
  is_folder: true; // Always true for folders
  report: string;
  items: (AttachmentFolder | AttachmentFile)[]; // Can contain both files and subfolders recursively
  is_checked?: boolean;
}

export interface ReportAttachementProps {
  id: number;
  name: string;
  file_type: string;
  file_size?: number;
  created_at: string;
  last_modified: string;
  url?: string;
  attachment_type: number | null;
  user_type: number;
  uploader_id: number;
  uploader_name: string;
  is_folder: boolean; // Always false for files
  is_checked?: boolean;
  parent_folder?: string | null;
  report?: string;
  items?: (AttachmentFolder | AttachmentFile)[];
}

export const requestInformationItemSchema = z.object({
  id: z.number(),
  case_manager: z.string(),
  department_name: z.string(),
  status: z.number(),
  due_date_formatted_date: z.string(),
  rfi_conversation: z.object({
    id: z.string(),
    recipient_full_name: z.string(),
    recipient_email: z.string(),
    conversation_subject: z.string(),
    messages: z.array(z.any()),
    case_manager_id: z.number().optional(),
    recipient_id: z.number().optional(),
    latest_message: z.string().optional(),
    unread_messages_count: z.number().optional(),
  }),
});
export type RequestInformationItem = z.infer<
  typeof requestInformationItemSchema
>;

export const requestInformationDetailsSchema = z.object({
  id: z.number(),
  recipient_email: z.string(),
  recipient_name: z.string(),
  details: z.string(),
  department_name: z.string(),
  status: z.number(),
  has_request_to_upload_file: z.boolean(),
  files: z
    .array(
      z.object({
        id: z.number(),
        original_file_name: z.string(),
        file_type: z.string(),
        url: z.string(),
      }),
    )
    .nullable(),
  due_date: z.string().datetime(),
  due_date_formatted_date: z.string(),
  subject: z.string(),
  closure_reason: z.string().nullable(),
  rfi_conversation: z.object({
    id: z.string(),
    recipient_full_name: z.string(),
    recipient_email: z.string(),
    conversation_subject: z.string(),
    messages: z.array(z.any()),
    case_manager_id: z.number().optional(),
    recipient_id: z.number().optional(),
    latest_message: z.string().optional(),
    unread_messages_count: z.number().optional(),
  }),
});
export type RequestInformationDetails = z.infer<
  typeof requestInformationDetailsSchema
>;

export const audioSegmentSchema = z.object({
  id: z.number(),
  transcript: z.string(),
  start_time: z.string(),
  end_time: z.string(),
  language_code: z.string(),
  speaker_label: z.string(),
  items: z.array(z.number()),
});
export type AudioSegment = z.infer<typeof audioSegmentSchema>;

const baseNodeSchema = z.object({
  id: z.string(),
  label: z.string(),
  fill: z.string(),
  record_id: z.union([z.number(), z.string()]),
  record_type: z.string(),
});

/**
 * Since nodes are a mix of report nodes and base nodes(which are nodes that `report nodes` attach to),
 * we create this specific schema to type for a report specific node.
 */
const reportNodeSchema = baseNodeSchema.extend({
  report_type: z.string(),
  department: z.string(),
  subject_individual: z.string(),
  country: z.string(),
  created_at: z.string().datetime(),
});

export const reportNetworkSchema = z.object({
  nodes: z.array(z.union([reportNodeSchema, baseNodeSchema])),
  edges: z.array(
    z.object({
      id: z.string(),
      source: z.string(),
      target: z.string(),
    }),
  ),
});
export type ReportNetwork = z.infer<typeof reportNetworkSchema>;
export type ReportNode = z.infer<typeof reportNodeSchema>;

const SEARCH_RESULT_FIELD_TYPES = {
  'comments.content': 'comments.content',
  'report_custom_field_values.value': 'report_custom_field_values.value',
  description_text: 'description_text',
  additional_informations: 'additional_informations',
} as const;
export type SearchResultFieldOrigin = keyof typeof SEARCH_RESULT_FIELD_TYPES;

export const reportSearchResultSchema = z.object({
  formatted_report_number: z.string(),
  reference_id: z.string(),
  field: z.preprocess(
    (val) => {
      return val
        ? z
          .string()
          .parse(val)
          .split(',')
          .map((v) => v.trim())
        : [];
    },
    z.array(z.literal('Investigator Notes').or(z.literal('Report'))),
  ),
  highlighted_text: z.record(
    z.nativeEnum(SEARCH_RESULT_FIELD_TYPES),
    z.array(z.string()),
  ),
  report_type_name: z.string(),
  user_full_name: z.string().optional(),
  status: z.number(),
});
export type ReportSearchResult = z.infer<typeof reportSearchResultSchema>;

export const piiRemovalMessageBoxPropsSchema = z.object({
  messageContent: z.string(),
  messageIndex: z.number(),
  messageId: z.string(),
});

export type PiiRemovalMessageBoxProps = z.infer<
  typeof piiRemovalMessageBoxPropsSchema
>;

export const riskScoreSchema = z.object({
  id: z.number(),
  risk_score: z.number(),
});

export const reportActionPayloadSchema = z.object({
  assignee: z.coerce.number(),
  department: z.coerce.number(),
  due_date: z.coerce.date(),
  followers: z.array(z.number()),
  linked_reports: z.array(z.string()),
  loss_value_currency: z.string(),
  loss_value: z.string(),
  priority: z.coerce.number(),
  report_type: z.coerce.number(),
  reporter: z.string(),
  status: z.coerce.number(),
  subject_individual: z.string(),
  suspected_department: z.string(),
  tags: z.array(z.string()),
  employee_sanction: z.string(),
  report_outcome: z.string(),
  description_text: z.string(),
  case_manager_fields_value: z.array(
    z.object({
      id: z.number(),
      value: z.string(),
    }),
  ),
  new_case_manager_field_value: z.array(
    z.object({
      tenant_case_manager_field: z.number(),
      value: z.string(),
    }),
  ),
  report_informations: z.array(riskScoreSchema),
  report_custom_field_values: z.array(riskScoreSchema),
});

export type ReportActionPayloadSchemaProps = z.infer<
  typeof reportActionPayloadSchema
>;

export const reportActionResponseSchema = z.object({
  assignee: z.number(),
  followers: z.array(z.number()),
  priority: z.number(),
  status: z.number(),
  tags: z.array(z.string()),
  report_type: z.number(),
  linked_reports: z.array(
    z.object({ reference_id: z.string(), formatted_report_number: z.string() }),
  ),
  due_date: z.string(),
  department: z.number(),
  suspected_department: z.string(),
  subject_individual: z.string(),
  is_case_locked_enabled: z.boolean(),
  is_whistleblower_feedback_enabled: z.boolean(),
  country: z.string(),
  full_name: z.string(),
  description_redacted_ranges: z.array(z.unknown()),
  report_outcome: z.string(),
  employee_sanction: z.string(),
  report_action_fields_value: z.array(
    z.object({
      id: z.number(),
      value: z.string(),
      field_title: z.string(),
      field_id: z.number(),
    }),
  ),
  loss_value: z.string(),
  loss_value_currency: z.string(),
  is_case_do_not_purge_enabled: z.boolean(),
  is_case_privileged_enabled: z.boolean(),
  description: z.object({
    can_edit_risk_score: z.boolean(),
    id: z.coerce.number(),
    risk_score: z.coerce.number(),
    description: z.string(),
  }),
  report_custom_field_values: z.array(
    z.object({
      tenant_report_custom_field: z.number(),
      value: z.string(),
      title: z.string(),
      report_field_name: z.string().nullable(),
      sequence: z.number(),
      conditional_logic_value: z.string(),
      id: z.number(),
      deleted_at: z.string(),
      get_risk_score: z.record(z.string(), z.number().nullable()),
      can_edit_risk_score: z.boolean(),
      conditional_logic_data: z.object({
        conditional_logic_title: z.string(),
        conditional_logic_answer: z.string(),
        risk_score: z.number().nullable(),
      }),
    }),
  ),
  risk_score: z.number(),
});

export type ReportActionResponseSchemaProps = z.infer<
  typeof reportActionResponseSchema
>;

//CASE ACTIONS

export enum ActionFieldType {
  'Text field' = 0,
  'Date field' = 1,
  'Dropdown field' = 2,
}
export const caseActionSchema = z.object({
  id: z.number(),
  title: z.string(),
  is_mandatory: z.boolean(),
  is_default: z.boolean(),
  type: z.nativeEnum(ActionFieldType),
  dropdown_choices: z.array(z.string().max(10)),
  order: z.number().nullable(),
  is_display_on_report_actions_enabled: z.boolean(),
  is_field_editable: z.boolean(),
  pre_defined_dropdown_choices: z.array(z.string()),
  is_choices_editable: z.boolean(),
  checked: z.boolean().optional().default(false),
});
export type CaseAction = z.infer<typeof caseActionSchema>;

export const addCaseActionPayloadSchema = z.object({
  title: z.string(),
  type: z.number(),
  dropdown_choices: z.array(z.string()).optional(),
});
export type AddCaseActionPayload = z.infer<typeof addCaseActionPayloadSchema>;

export const updateCaseActionPayloadSchema = z.object({
  id: z.coerce.number(),
  title: z.string().optional(),
  dropdown_choices: z.array(z.string()).optional(),
});
export type UpdateCaseActionPayload = z.infer<
  typeof updateCaseActionPayloadSchema
>;
